import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { Tabs } from "@/components/Tabs";
import { StatusChip } from "@/components/StatusChip";
import { Drawer } from "@/components/Drawer";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import roleBranches from "@/constants/roleBranches.json";
import { Plus, X, Clock, AlertTriangle, CheckCircle2, XCircle, Ban, Sparkles, RefreshCw, ChevronDown, ChevronRight, ExternalLink, MoreHorizontal } from "lucide-react";
import { CreateNjmCollaborationModal } from "@/components/community/CreateNjmCollaborationModal";
import { CollaborationDetailModal } from "@/components/community/CollaborationDetailModal";

// NJM Build Opportunities Wave 2 (2026-09) — preview-only staff admin UI. Source-neutral
// model: this wave is ADMIN-CREATED ONLY, no Notion ingestion. Discipline list reused from the
// existing role taxonomy (constants/roleBranches.json BRANCHES keys) — no new taxonomy.
const DISCIPLINES = Object.keys(roleBranches.BRANCHES || {}).sort();
const WORK_MODES = ["onsite", "remote", "hybrid"];
const EMPLOYMENT_TYPES = ["not_specified", "freelance", "project", "contract", "full_time", "part_time", "internship"];
const NJM_RELATIONSHIPS = ["njm_opportunity", "njm_connection", "community"];
const RELATIONSHIP_LABEL = { njm_opportunity: "NJM Opportunity", njm_connection: "NJM Connection", community: "Community" };
const STATUSES = ["candidate", "published", "rejected", "expired", "closed", "source_unavailable", "archived"];
const CANDIDATE_OR_PUBLISHED = new Set(["candidate", "published"]);

// Interest-tab (Support Requests) statuses are a DIFFERENT vocabulary from the opportunity
// statuses above (only "closed" happens to be spelled the same) — kept as its own local tone
// map rather than reusing StatusChip's shared statusTone() so none of these silently pick up
// a different meaning (e.g. "sent" is blue in the shared map but was always plain grey here).
// Values below are the exact same grey/slate-ish appearance the old hardcoded hex rendered,
// just on Design System 2.0 CSS var tokens instead of hex.
const STATUS_STYLE = {
  candidate: "bg-tone-gold-bg text-tone-gold-text", published: "bg-tone-green-bg text-tone-green-text",
  rejected: "bg-tone-red-bg text-tone-red-text", expired: "bg-tone-grey-bg text-tone-grey-text",
  closed: "bg-tone-grey-bg text-tone-grey-text", source_unavailable: "bg-tone-gold-bg text-tone-gold-text",
  archived: "bg-tone-grey-bg text-tone-grey-text",
};

const EMPTY_FORM = {
  title: "", role: "", organisation: "", discipline: [], city: "", country: "",
  work_mode: "onsite", employment_type: "not_specified", public_summary: "", listed_pay: "", experience: "",
  source_type: "admin", source_channel: "", source_url: "", source_posted_at: "", deadline: "", tags: "",
  njm_relationship: "community",
  internal: {
    source_record_id: "", contact_name: "", contact_email: "", contact_phone: "", contact_linkedin: "", notes: "", review_notes: "",
    // Ingestion-facing (2026-09-27 addendum) — staff-only.
    source_record_url: "", source_lead_id: "", opportunity_class: "", source_portal_status: "", why_relevant: "",
  },
};

function daysUntil(iso) {
  if (!iso) return null;
  const diff = (new Date(iso).getTime() - Date.now()) / 86400000;
  return Math.ceil(diff);
}

function formatLocation(item) {
  return [item.city, item.country].filter(Boolean).join(", ") || null;
}

// Phase 6 (2026-10, Job D) — EXPIRES column sort, ascending, missing/non-eligible last.
function sortByExpiryAsc(items) {
  return [...items].sort((a, b) => {
    const ea = CANDIDATE_OR_PUBLISHED.has(a.status) && a.expires_at ? new Date(a.expires_at).getTime() : Infinity;
    const eb = CANDIDATE_OR_PUBLISHED.has(b.status) && b.expires_at ? new Date(b.expires_at).getTime() : Infinity;
    return ea - eb;
  });
}

function ExpiresCell({ item, testId }) {
  if (!CANDIDATE_OR_PUBLISHED.has(item.status) || !item.expires_at) return <span className="text-faint text-xs">—</span>;
  const days = daysUntil(item.expires_at);
  if (days == null) return <span className="text-faint text-xs">—</span>;
  const soon = days <= 7;
  return (
    <span data-testid={testId || `opportunity-expiry-${item.id}`} className={`inline-flex items-center gap-1 whitespace-nowrap text-xs ${soon ? "text-tone-red-text font-medium" : "text-ink-soft"}`}>
      <Clock className="h-3 w-3" />{days <= 0 ? "expires today" : `in ${days}d`}
    </span>
  );
}

function DisciplineChips({ discipline }) {
  const list = discipline || [];
  if (list.length === 0) return <span className="text-faint text-xs">—</span>;
  const shown = list.slice(0, 2);
  const extra = list.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((d) => <span key={d} className="rounded-full bg-surface2 px-2 py-0.5 text-[10px] text-ink-soft">{d}</span>)}
      {extra > 0 && <span className="text-[10px] text-faint">+{extra}</span>}
    </div>
  );
}

// "⋯" for a row/drawer — Manage through NJM only ever sets `promoted_to_collaboration_id`
// (collaboration.py's create_njm_collaboration, ~line 600) and never touches status or
// njm_relationship (code-verified, not runtime-verified). Close (published→closed) is kept
// here even though frame 11 doesn't show it for published rows — it's an existing capability
// (modules/build_opportunities.py `close_opportunity`, published-only) with no replacement in
// the frame, so dropping it would be a functional regression, not a visual one. See PR notes.
function OpportunityOverflowMenu({ item, onEdit, onManageThroughNjm, onClose, onArchive, testId }) {
  const showManage = CANDIDATE_OR_PUBLISHED.has(item.status) && !item.promoted_to_collaboration_id;
  const showClose = item.status === "published";
  const showArchive = item.status !== "archived";
  const tid = testId || `opportunity-overflow-${item.id}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="More actions" data-testid={tid}
          className="inline-flex items-center justify-center rounded-md border border-hairline bg-paper h-7 w-7 hover:border-brand shrink-0">
          <MoreHorizontal className="h-3.5 w-3.5 text-ink-soft" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 p-1.5">
        <button type="button" data-testid={`opportunity-edit-${item.id}`} onClick={onEdit} className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] text-ink-soft hover:bg-surface2">Edit</button>
        {showManage && (
          <button type="button" data-testid={`opportunity-manage-through-njm-${item.id}`} onClick={onManageThroughNjm} className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] text-ink-soft hover:bg-surface2">Manage through NJM</button>
        )}
        {showClose && (
          <button type="button" data-testid={`opportunity-close-${item.id}`} onClick={onClose} className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] text-ink-soft hover:bg-surface2"><Ban className="h-3.5 w-3.5" /> Close</button>
        )}
        {showArchive && (
          <button type="button" data-testid={`opportunity-archive-${item.id}`} onClick={onArchive} className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] text-tone-red-text hover:bg-tone-red-bg">Archive</button>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function OpportunityTableRow({ item, onOpen, onPublish, onReject, onEdit, onManageThroughNjm, onClose, onArchive, onViewCollab, collabTitleById }) {
  const location = formatLocation(item);
  return (
    <tr data-testid={`opportunity-row-${item.id}`} onClick={() => onOpen(item)}
      className="h-12 cursor-pointer border-b border-hairline last:border-b-0 hover:bg-surface2">
      <td className="px-3 py-2 min-w-0 max-w-[260px]">
        <div className="truncate font-medium text-sm text-ink">{item.title}</div>
        {item.listed_pay && <div className="truncate text-[11px] text-faint">Pay as listed: {item.listed_pay}</div>}
      </td>
      <td className="px-3 py-2 text-xs text-ink-soft whitespace-nowrap">{item.organisation}</td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">{location ? <span className="text-ink-soft">{location}</span> : <span className="text-faint">Not listed</span>}</td>
      <td className="px-3 py-2"><DisciplineChips discipline={item.discipline} /></td>
      <td className="px-3 py-2"><ExpiresCell item={item} /></td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <StatusChip status={item.status} testId={`opportunity-status-${item.id}`} />
          {item.internal?.opportunity_class === "both" && (
            <span data-testid={`opportunity-njm-bid-badge-${item.id}`} className="inline-flex items-center gap-1 rounded-full bg-tone-blue-bg px-2 py-0.5 text-[10px] font-medium text-tone-blue-text">
              <Sparkles className="h-3 w-3" />Possible NJM bid
            </span>
          )}
          {item.promoted_to_collaboration_id && (
            <button type="button" data-testid={`opportunity-managed-through-njm-${item.id}`}
              onClick={(e) => { e.stopPropagation(); onViewCollab(item.promoted_to_collaboration_id); }}
              className="inline-flex items-center gap-1 rounded-full bg-brand/20 px-2 py-0.5 text-[10px] font-medium text-ink hover:bg-brand/30">
              Managed through NJM: {collabTitleById[item.promoted_to_collaboration_id] || "…"} <ExternalLink className="h-2.5 w-2.5" />
            </button>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="inline-flex items-center justify-end gap-1.5">
          {item.status === "candidate" && (
            <>
              <button type="button" data-testid={`opportunity-publish-${item.id}`} onClick={() => onPublish(item)}
                className="rounded-md bg-brand px-2.5 py-1 text-[11px] font-semibold text-ink hover:bg-brand-dim">Publish</button>
              <button type="button" data-testid={`opportunity-reject-${item.id}`} onClick={() => onReject(item)}
                className="rounded-md border border-tone-red-text/30 px-2.5 py-1 text-[11px] font-medium text-tone-red-text hover:bg-tone-red-bg">Reject</button>
            </>
          )}
          {item.status === "published" && (
            <span data-testid={`opportunity-live-note-${item.id}`} className="text-[11px] text-faint whitespace-nowrap">Live for freelancers</span>
          )}
          <OpportunityOverflowMenu item={item} onEdit={() => onEdit(item)} onManageThroughNjm={() => onManageThroughNjm(item)} onClose={() => onClose(item)} onArchive={() => onArchive(item)} />
        </div>
      </td>
    </tr>
  );
}

function OpportunityReviewDrawer({ item, onClose: closeDrawer, onPublish, onReject, onEdit, onManageThroughNjm, onCloseOpportunity, onArchive, onRelationshipChange, savingRelationship, onViewCollab, collabTitleById }) {
  if (!item) return null;
  const location = formatLocation(item);
  const showManage = CANDIDATE_OR_PUBLISHED.has(item.status) && !item.promoted_to_collaboration_id;
  const showPublishReject = item.status === "candidate";
  const showClose = item.status === "published";
  const showArchive = item.status !== "archived";
  return (
    <Drawer open={!!item} onOpenChange={(v) => !v && closeDrawer()} testId="opportunity-review-drawer" title={item.title}>
      <div className="space-y-4" data-testid="opportunity-review-drawer-body">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusChip status={item.status} testId="opportunity-review-drawer-status" />
          {item.status === "published" && <span className="text-xs text-faint" data-testid="opportunity-review-drawer-live-note">Live for freelancers</span>}
          {item.promoted_to_collaboration_id && (
            <button type="button" data-testid="opportunity-review-drawer-view-collab" onClick={() => onViewCollab(item.promoted_to_collaboration_id)}
              className="inline-flex items-center gap-1 rounded-full bg-brand/20 px-2 py-0.5 text-[11px] font-medium text-ink hover:bg-brand/30">
              Managed through NJM: {collabTitleById[item.promoted_to_collaboration_id] || "…"} <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="text-sm text-ink-soft">{item.role} · {item.organisation}{location ? ` · ${location}` : ""}</div>

        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div><dt className="text-[11px] uppercase tracking-wider text-faint">Source</dt><dd className="text-ink mt-0.5">{item.organisation}</dd></div>
          <div><dt className="text-[11px] uppercase tracking-wider text-faint">Location</dt><dd className="text-ink mt-0.5">{location || "Not listed"}</dd></div>
          <div><dt className="text-[11px] uppercase tracking-wider text-faint">Expires</dt><dd className="mt-0.5"><ExpiresCell item={item} testId="opportunity-review-drawer-expiry" /></dd></div>
          {item.listed_pay && <div><dt className="text-[11px] uppercase tracking-wider text-faint">Pay as listed</dt><dd className="text-ink mt-0.5">{item.listed_pay}</dd></div>}
          {item.experience && <div><dt className="text-[11px] uppercase tracking-wider text-faint">Experience</dt><dd className="text-ink mt-0.5">{item.experience}</dd></div>}
        </dl>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-faint mb-1">Disciplines</div>
          <div className="flex flex-wrap gap-1.5">
            {(item.discipline || []).map((d) => <span key={d} className="rounded-full bg-surface2 px-2 py-0.5 text-[11px] text-ink-soft">{d}</span>)}
          </div>
        </div>

        {item.public_summary && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-faint mb-1">Summary</div>
            <p className="text-sm text-ink-soft whitespace-pre-wrap">{item.public_summary}</p>
          </div>
        )}

        <div>
          <label className="text-[11px] uppercase tracking-wider text-faint" htmlFor="opportunity-review-drawer-relationship-select">NJM relationship</label>
          <select id="opportunity-review-drawer-relationship-select" data-testid="opportunity-review-drawer-relationship-select" value={item.njm_relationship} disabled={savingRelationship}
            onChange={(e) => onRelationshipChange(item, e.target.value)}
            className="mt-1 w-full rounded-md border border-hairline px-3 py-2 text-sm bg-paper text-ink disabled:opacity-50">
            {NJM_RELATIONSHIPS.map((r) => <option key={r} value={r}>{RELATIONSHIP_LABEL[r]}</option>)}
          </select>
        </div>

        {item.source_url && (
          <a href={item.source_url} target="_blank" rel="noreferrer" data-testid="opportunity-review-drawer-source-link" className="inline-flex items-center gap-1 text-xs text-gold-text hover:underline">
            Open source listing <ExternalLink className="h-3 w-3" />
          </a>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-hairline">
          <button type="button" data-testid="opportunity-review-drawer-edit" onClick={() => onEdit(item)} className="rounded-md border border-hairline px-3 py-1.5 text-xs text-ink-soft hover:border-brand">Edit</button>
          {showManage && (
            <button type="button" data-testid="opportunity-review-drawer-manage-through-njm" onClick={() => onManageThroughNjm(item)} className="rounded-md border border-brand px-3 py-1.5 text-xs font-medium text-ink hover:bg-brand/10">Manage through NJM</button>
          )}
          {showPublishReject && (
            <button type="button" data-testid="opportunity-review-drawer-reject" onClick={() => onReject(item)} className="rounded-md border border-tone-red-text/30 px-3 py-1.5 text-xs font-medium text-tone-red-text hover:bg-tone-red-bg">Reject</button>
          )}
          {(showClose || showArchive) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" aria-label="More actions" data-testid="opportunity-review-drawer-overflow"
                  className="inline-flex items-center justify-center rounded-md border border-hairline bg-paper h-8 w-8 hover:border-brand">
                  <MoreHorizontal className="h-4 w-4 text-ink-soft" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44 p-1.5">
                {showClose && <button type="button" data-testid="opportunity-review-drawer-close" onClick={() => onCloseOpportunity(item)} className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] text-ink-soft hover:bg-surface2">Close</button>}
                {showArchive && <button type="button" data-testid="opportunity-review-drawer-archive" onClick={() => onArchive(item)} className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] text-tone-red-text hover:bg-tone-red-bg">Archive</button>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {showPublishReject && (
            <button type="button" data-testid="opportunity-review-drawer-publish" onClick={() => onPublish(item)} className="ml-auto rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-ink hover:bg-brand-dim">Publish</button>
          )}
        </div>
      </div>
    </Drawer>
  );
}

function OpportunityForm({ initial, onCancel, onSaved, onPublish, onReject, onManageThroughNjm, collabTitle, onViewCollab }) {
  // Null-safe merge (2026-09-27 addendum) — an edited item's `internal.*` fields come back as
  // null from the server (unset), never "" — every input below is a controlled string input.
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM, ...initial,
    tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
    internal: { ...EMPTY_FORM.internal, ...Object.fromEntries(Object.entries(initial.internal || {}).map(([k, v]) => [k, v ?? ""])) },
  }));
  const [saving, setSaving] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);
  const isEdit = !!initial.id;
  // Addendum (2026-09-29, founder walkthrough w/ Bavna): both internal boxes are noisy on every
  // edit — collapsed by default, staff expands only when they actually need them.
  const [internalOpen, setInternalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Single-review-place (2026-09-27): a Class-check row in the Shadow Ingestion tab now opens
  // THIS same edit panel (via onOpenItem) instead of only offering Correct/Incorrect inline —
  // this local state lets the banner below update immediately after a verify click without
  // needing the parent list to reload first.
  const [classVerified, setClassVerified] = useState(initial.internal?.class_verified ?? null);
  const [verifying, setVerifying] = useState(false);
  const verifyClass = async (correct) => {
    setVerifying(true);
    try {
      await api.post(`/v1/build-opportunities/${initial.id}/verify-class`, { correct });
      setClassVerified(correct);
      toast.success("Recorded");
    } catch {
      toast.error("Couldn't record class verification");
    } finally {
      setVerifying(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setInternal = (k, v) => setForm((f) => ({ ...f, internal: { ...f.internal, [k]: v } }));
  const toggleDiscipline = (d) => setForm((f) => ({ ...f, discipline: f.discipline.includes(d) ? f.discipline.filter((x) => x !== d) : [...f.discipline, d] }));

  // Dedupe helper (2026-09): warn the admin on a probable duplicate rather than blocking —
  // debounced live check as they type organisation/role/city/source_url.
  useEffect(() => {
    if (!form.organisation.trim() && !form.role.trim() && !form.source_url.trim()) { setDupWarning(null); return; }
    const t = setTimeout(() => {
      const params = { organisation: form.organisation, role: form.role, city: form.city, source_url: form.source_url };
      if (isEdit) params.exclude_id = initial.id;
      api.get("/v1/build-opportunities/duplicate-check", { params }).then((r) => setDupWarning(r.data.duplicate ? r.data.existing : null)).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [form.organisation, form.role, form.city, form.source_url, isEdit, initial.id]);

  const save = async () => {
    if (!form.title.trim() || !form.role.trim() || !form.organisation.trim() || form.discipline.length === 0) {
      toast.error("Title, role, organisation and at least one discipline are required");
      return;
    }
    setSaving(true);
    const payload = { ...form, tags: (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      const r = isEdit
        ? await api.patch(`/v1/build-opportunities/${initial.id}`, payload)
        : await api.post("/v1/build-opportunities", payload);
      if (r.data.duplicate_warning) {
        toast.warning(`Saved — but possible duplicate of "${r.data.duplicate_warning.title}" (${r.data.duplicate_warning.status})`);
      } else {
        toast.success(isEdit ? "Opportunity updated" : "Opportunity created");
      }
      onSaved(r.data.item);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code ? String(e.response.data.detail.code).replaceAll("_", " ") : "Couldn't save this opportunity");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="opportunity-form" className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-3 sm:p-6">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl my-4 sm:my-8">
        <div className="flex items-center justify-between border-b border-hairline px-4 sm:px-6 py-4">
          <h2 className="text-lg font-semibold text-ink">{isEdit ? "Edit opportunity" : "New opportunity"}</h2>
          <button data-testid="opportunity-form-close" onClick={onCancel}><X className="h-5 w-5 text-ink-soft" /></button>
        </div>
        <div className="space-y-4 px-4 sm:px-6 py-5">
          {isEdit && initial.internal?.opportunity_class && (
            <div data-testid="opportunity-class-review-banner" className="rounded-md border border-indigo-200 bg-indigo-50 p-3">
              <div className="flex items-center gap-2 text-sm text-indigo-900">
                <Sparkles className="h-4 w-4" />
                <span>Grok classified this as: <b>{initial.internal.opportunity_class === "both" ? "Both" : "Freelancer opportunity"}</b></span>
              </div>
              <p className="mt-1 text-[11px] text-indigo-700">Freelancer opportunity = the hirer wants one person for a role. Both = the brief could go to an individual OR a production company (e.g. RFP open to agencies).</p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <button type="button" data-testid="opportunity-class-correct" disabled={verifying} onClick={() => verifyClass(true)} className="rounded-md border border-emerald-300 px-2.5 py-1 text-xs text-emerald-700 disabled:opacity-50">Correct</button>
                <button type="button" data-testid="opportunity-class-incorrect" disabled={verifying} onClick={() => verifyClass(false)} className="rounded-md border border-red-300 px-2.5 py-1 text-xs text-red-700 disabled:opacity-50">Incorrect</button>
                {initial.status === "candidate" && (
                  <>
                    <span className="mx-1 text-indigo-300">|</span>
                    <button type="button" data-testid="opportunity-class-publish" onClick={onPublish} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white"><CheckCircle2 className="h-3 w-3" />Publish</button>
                    <button type="button" data-testid="opportunity-class-reject" onClick={onReject} className="inline-flex items-center gap-1 rounded-md border border-red-300 px-2.5 py-1 text-xs text-red-600"><XCircle className="h-3 w-3" />Reject</button>
                  </>
                )}
              </div>
              {classVerified != null && (
                <div data-testid="opportunity-class-verified-note" className="mt-1.5 text-[11px] text-indigo-600">Already verified as {classVerified ? "correct" : "incorrect"}.</div>
              )}
            </div>
          )}
          {isEdit && initial.promoted_to_collaboration_id && (
            <div data-testid="opportunity-managed-through-njm-banner" className="flex items-center justify-between gap-2 rounded-md border border-brand bg-brand/10 px-3 py-2.5">
              <span className="text-sm text-ink">Managed through NJM: <b>{collabTitle || "…"}</b></span>
              <button type="button" data-testid="opportunity-view-collab-link" onClick={onViewCollab} className="inline-flex items-center gap-1 text-xs font-semibold text-ink hover:underline">
                View <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          )}
          {dupWarning && (
            <div data-testid="opportunity-duplicate-warning" className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Possible duplicate of <b>{dupWarning.title}</b> ({dupWarning.status}). You can still save — just double-check first.</span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="opportunity-title-input" placeholder="Title *" value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm sm:col-span-2" />
            <input data-testid="opportunity-role-input" placeholder="Role *" value={form.role} onChange={(e) => set("role", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
            <input data-testid="opportunity-organisation-input" placeholder="Organisation *" value={form.organisation} onChange={(e) => set("organisation", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
            <input data-testid="opportunity-city-input" placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
            <input data-testid="opportunity-country-input" placeholder="Country" value={form.country} onChange={(e) => set("country", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
            <select data-testid="opportunity-work-mode-select" value={form.work_mode} onChange={(e) => set("work_mode", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm">
              {WORK_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select data-testid="opportunity-employment-type-select" value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm">
              {EMPLOYMENT_TYPES.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
            </select>
            <select data-testid="opportunity-njm-relationship-select" value={form.njm_relationship} onChange={(e) => set("njm_relationship", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm">
              {NJM_RELATIONSHIPS.map((m) => <option key={m} value={m}>{m.replaceAll("_", " ")}</option>)}
            </select>
            <input data-testid="opportunity-deadline-input" type="date" placeholder="Deadline" value={(form.deadline || "").slice(0, 10)} onChange={(e) => set("deadline", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
          </div>

          <div>
            <div className="mb-1.5 text-xs font-medium text-ink-soft">Discipline * (from existing role taxonomy)</div>
            <div className="flex flex-wrap gap-1.5">
              {DISCIPLINES.map((d) => (
                <button key={d} type="button" data-testid={`opportunity-discipline-chip-${d}`} onClick={() => toggleDiscipline(d)}
                  className={`rounded-full border px-2.5 py-1 text-xs ${form.discipline.includes(d) ? "border-brand bg-brand/15 text-ink" : "border-hairline text-ink-soft"}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>

          <textarea data-testid="opportunity-summary-input" placeholder="Public summary (visible to freelancers later — no emails/phone/LinkedIn profile links)" value={form.public_summary} onChange={(e) => set("public_summary", e.target.value)} rows={3} className="w-full rounded-md border border-hairline px-3 py-2 text-sm" />
          <input data-testid="opportunity-listed-pay-input" placeholder="Pay as listed (exactly as printed on the listing, e.g. Rs 80,000/day)" value={form.listed_pay} onChange={(e) => set("listed_pay", e.target.value)} className="w-full rounded-md border border-hairline px-3 py-2 text-sm" />
          <input data-testid="opportunity-experience-input" placeholder="Experience required (e.g. 3+ years, as listed)" value={form.experience} onChange={(e) => set("experience", e.target.value)} className="w-full rounded-md border border-hairline px-3 py-2 text-sm" />
          <input data-testid="opportunity-tags-input" placeholder="Tags (comma-separated)" value={form.tags} onChange={(e) => set("tags", e.target.value)} className="w-full rounded-md border border-hairline px-3 py-2 text-sm" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="opportunity-source-url-input" placeholder="Source URL (original job listing — never a LinkedIn profile link)" value={form.source_url} onChange={(e) => set("source_url", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
            <input data-testid="opportunity-source-channel-input" placeholder="Source channel (e.g. LinkedIn Jobs, Job board, Public RFP / TOR)" value={form.source_channel} onChange={(e) => set("source_channel", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
          </div>

          <div className="rounded-md border border-red-200 bg-red-50/50 p-3">
            <div className="flex items-center justify-between gap-2">
              <button type="button" data-testid="opportunity-internal-notes-toggle" onClick={() => setInternalOpen((v) => !v)} className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-red-700">
                {internalOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Internal notes (optional — NJM only)
              </button>
              {form.internal.source_record_url && (
                <a href={form.internal.source_record_url} target="_blank" rel="noreferrer" data-testid="opportunity-internal-open-notion-row" className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:underline">
                  Open Notion row <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            <p className="mt-1 text-[11px] text-red-700/70">Contact details are intentionally not imported from Notion. Open the source row if you need them.</p>
            {internalOpen && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input data-testid="opportunity-internal-contact-name" placeholder="Contact name" value={form.internal.contact_name} onChange={(e) => setInternal("contact_name", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
                <input data-testid="opportunity-internal-contact-email" placeholder="Contact email" value={form.internal.contact_email} onChange={(e) => setInternal("contact_email", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
                <input data-testid="opportunity-internal-contact-phone" placeholder="Contact phone" value={form.internal.contact_phone} onChange={(e) => setInternal("contact_phone", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
                <input data-testid="opportunity-internal-contact-linkedin" placeholder="Contact LinkedIn" value={form.internal.contact_linkedin} onChange={(e) => setInternal("contact_linkedin", e.target.value)} className="rounded-md border border-hairline px-3 py-2 text-sm" />
                <textarea data-testid="opportunity-internal-notes" placeholder="Notes" value={form.internal.notes} onChange={(e) => setInternal("notes", e.target.value)} rows={2} className="rounded-md border border-hairline px-3 py-2 text-sm sm:col-span-2" />
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-red-200">
              <button type="button" data-testid="opportunity-import-details-toggle" onClick={() => setImportOpen((v) => !v)} className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-red-700/80">
                {importOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} Import details (read-only)
              </button>
              {importOpen && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input data-testid="opportunity-internal-source-record-url" placeholder="Source record URL" value={form.internal.source_record_url} readOnly disabled className="rounded-md border border-hairline bg-gray-100 px-3 py-2 text-sm text-ink-soft" />
                  <input data-testid="opportunity-internal-source-lead-id" placeholder="Source lead ID" value={form.internal.source_lead_id} readOnly disabled className="rounded-md border border-hairline bg-gray-100 px-3 py-2 text-sm text-ink-soft" />
                  <input data-testid="opportunity-internal-opportunity-class" placeholder="Opportunity class" value={form.internal.opportunity_class ? form.internal.opportunity_class.replaceAll("_", " ") : "unset"} readOnly disabled className="rounded-md border border-hairline bg-gray-100 px-3 py-2 text-sm text-ink-soft" />
                  <input data-testid="opportunity-internal-source-portal-status" placeholder="Source portal status" value={form.internal.source_portal_status} readOnly disabled className="rounded-md border border-hairline bg-gray-100 px-3 py-2 text-sm text-ink-soft" />
                  <textarea data-testid="opportunity-internal-why-relevant" placeholder="Why relevant" value={form.internal.why_relevant} readOnly disabled rows={2} className="rounded-md border border-hairline bg-gray-100 px-3 py-2 text-sm text-ink-soft sm:col-span-2" />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-hairline px-4 sm:px-6 py-4">
          <button data-testid="opportunity-form-cancel" onClick={onCancel} className="rounded-md border border-hairline px-4 py-2 text-sm text-ink-soft">Cancel</button>
          {isEdit && ["candidate", "published"].includes(initial.status) && !initial.promoted_to_collaboration_id && onManageThroughNjm && (
            <button type="button" data-testid="opportunity-form-manage-through-njm" onClick={onManageThroughNjm} className="rounded-md border border-brand px-4 py-2 text-sm font-medium text-ink hover:bg-brand/10">Manage through NJM</button>
          )}
          <button data-testid="opportunity-form-save" onClick={save} disabled={saving} className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-ink disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function ShadowIngestionTab({ refreshKey, onDataChanged, onOpenItem }) {
  const [metrics, setMetrics] = useState(null);
  const [runs, setRuns] = useState([]);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      api.get("/v1/build-opportunities/ingestion/metrics"),
      api.get("/v1/build-opportunities/ingestion/runs"),
      api.get("/v1/build-opportunities"),
    ]).then(([m, r, o]) => {
      setMetrics(m.data);
      setRuns(r.data.items || []);
      setItems((o.data.items || []).filter((i) => i.source_type === "njm_lite_tracker" && i.internal?.opportunity_class && i.internal?.class_verified == null));
    }).catch(() => toast.error("Couldn't load shadow ingestion data")).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/v1/build-opportunities/ingestion/run");
      toast.success(`Run complete — ${r.data.created_count} created, ${r.data.updated_count} updated, ${r.data.rows_read} read`);
      load();
      onDataChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code ? String(e.response.data.detail.code).replaceAll("_", " ") : "Ingestion run failed");
    } finally {
      setRunning(false);
    }
  };

  const verifyClass = async (id, correct) => {
    try {
      await api.post(`/v1/build-opportunities/${id}/verify-class`, { correct });
      toast.success("Recorded");
      load();
      onDataChanged?.();
    } catch {
      toast.error("Couldn't record class verification");
    }
  };

  if (loading) return <div className="py-10 text-center text-sm text-ink-soft">Loading…</div>;

  const M = metrics || {};
  const metricCards = [
    { key: "total-ingested", label: "Total ingested", value: M.total_ingested ?? 0 },
    { key: "pii-scrubbed", label: "PII scrubbed", value: M.pii_scrub_count ?? 0 },
    { key: "possible-duplicates", label: "Possible duplicates", value: M.cross_run_duplicate_count ?? 0 },
    { key: "invalid-urls", label: "Invalid URLs", value: M.invalid_url_count ?? 0 },
    { key: "not-specified-rate", label: "Not-specified employment", value: `${Math.round((M.not_specified_rate ?? 0) * 100)}%` },
    { key: "business-lead-leakage", label: "Business-lead leakage", value: M.business_lead_leakage ?? 0 },
    { key: "class-accuracy", label: "Class accuracy", value: M.class_accuracy != null ? `${Math.round(M.class_accuracy * 100)}% (${M.class_correct_count}/${M.class_verified_count})` : "Not yet verified" },
  ];

  return (
    <div data-testid="shadow-ingestion-tab" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-ink">Shadow ingestion — NJM Lite Tracker (Notion)</h2>
        <button data-testid="ingestion-run-now-button" onClick={runNow} disabled={running} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-medium text-ink disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} />{running ? "Running…" : "Run now"}
        </button>
      </div>
      <p data-testid="shadow-ingestion-tab-helper" className="text-xs text-ink-soft -mt-2">Import health + Grok accuracy check. Daily review happens in the Opportunities tab.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {metricCards.map((m) => (
          <div key={m.key} data-testid={`ingestion-metric-${m.key}`} className="rounded-md border border-hairline p-3">
            <div className="text-[11px] text-ink-soft">{m.label}</div>
            <div className="text-lg font-semibold text-ink">{m.value}</div>
          </div>
        ))}
      </div>

      {M.last_run?.stale_feed_warning && (
        <div data-testid="ingestion-stale-feed-warning" className="flex items-center gap-2 rounded-md border border-tone-gold-text/30 bg-tone-gold-bg px-3 py-2 text-sm text-tone-gold-text">
          <AlertTriangle className="h-4 w-4" />Feed looks stale — the last run found no new or updated rows.
        </div>
      )}

      {items.length > 0 && (
        <div>
          <div className="mb-2 text-sm font-medium text-ink">Class check — is this really a freelancer opportunity / NJM bid?</div>
          <div className="space-y-2">
            {items.map((item) => (
              <div key={item.id} data-testid={`ingestion-class-check-${item.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-hairline p-2.5">
                <button type="button" data-testid={`ingestion-class-check-open-${item.id}`} onClick={() => onOpenItem?.(item)} className="text-sm text-ink min-w-0 truncate text-left hover:underline">{item.title} <span className="text-ink-soft">— {item.internal.opportunity_class.replaceAll("_", " ")}</span></button>
                <div className="flex gap-1.5 shrink-0">
                  <button type="button" data-testid={`ingestion-class-correct-${item.id}`} onClick={() => verifyClass(item.id, true)} className="rounded-md border border-tone-green-text/30 px-2.5 py-1 text-xs text-tone-green-text">Correct</button>
                  <button type="button" data-testid={`ingestion-class-incorrect-${item.id}`} onClick={() => verifyClass(item.id, false)} className="rounded-md border border-tone-red-text/30 px-2.5 py-1 text-xs text-tone-red-text">Incorrect</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="mb-2 text-sm font-medium text-ink">Run history</div>
        {runs.length === 0 ? (
          <div data-testid="ingestion-run-history-empty" className="text-sm text-ink-soft">No runs yet.</div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-hairline">
            <table className="w-full text-xs">
              <thead className="bg-surface2 text-ink-soft">
                <tr>
                  <th className="px-2 py-1.5 text-left">Run at</th>
                  <th className="px-2 py-1.5 text-left">Triggered by</th>
                  <th className="px-2 py-1.5 text-left">Status</th>
                  <th className="px-2 py-1.5 text-right">Read</th>
                  <th className="px-2 py-1.5 text-right">Created</th>
                  <th className="px-2 py-1.5 text-right">Updated</th>
                  <th className="px-2 py-1.5 text-left">Skipped</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} data-testid={`ingestion-run-row-${r.id}`} className="border-t border-hairline">
                    <td className="px-2 py-1.5">{new Date(r.run_at).toLocaleString()}</td>
                    <td className="px-2 py-1.5">{r.triggered_by}</td>
                    <td className="px-2 py-1.5">{r.status}{r.stale_feed_warning ? " ⚠︎" : ""}</td>
                    <td className="px-2 py-1.5 text-right">{r.rows_read}</td>
                    <td className="px-2 py-1.5 text-right">{r.created_count}</td>
                    <td className="px-2 py-1.5 text-right">{r.updated_count}</td>
                    <td className="px-2 py-1.5">{Object.entries(r.skipped_by_reason || {}).map(([k, v]) => `${k}:${v}`).join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const INTEREST_STATUSES = ["sent", "reviewing", "njm_reaching_out", "apply_directly_recommended", "introduced", "closed"];
const INTEREST_STATUS_LABEL = {
  sent: "Sent", reviewing: "Reviewing", njm_reaching_out: "NJM reaching out",
  apply_directly_recommended: "Apply directly recommended", introduced: "Introduced", closed: "Closed",
};

function InterestTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [reasonDrafts, setReasonDrafts] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/v1/build-opportunities/interests").then((r) => {
      const loaded = r.data.items || [];
      setItems(loaded);
      // Dashboard Fixes (2026-11), Item 2 — opening this tab is "the PM opened it": clear the
      // Dashboard "Support Request" item for every currently-loaded NEW (status="sent") row.
      // Fire-and-forget, never blocks the list render.
      loaded.filter((i) => i.status === "sent").forEach((i) => {
        api.post(`/v1/build-opportunities/interests/${i.id}/mark-read`).catch(() => {});
      });
    })
      .catch(() => toast.error("Couldn't load applications & support requests")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/v1/build-opportunities/interests/${id}/status`, { status, reason: reasonDrafts[id] || null });
      toast.success("Status updated");
      setReasonDrafts((d) => ({ ...d, [id]: "" }));
      load();
      window.__njmRefreshBadges?.();
    } catch {
      toast.error("Couldn't update status");
    }
  };

  const filtered = statusFilter ? items.filter((i) => i.status === statusFilter) : items;

  if (loading) return <div className="py-10 text-center text-sm text-ink-soft">Loading…</div>;

  return (
    <div data-testid="interest-tab" className="space-y-4">
      <p data-testid="interest-tab-helper" className="text-xs text-ink-soft">Each status change shows up in the freelancer's My Work.</p>
      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="interest-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-md border border-hairline px-2.5 py-1.5 text-xs">
          <option value="">All statuses</option>
          {INTEREST_STATUSES.map((s) => <option key={s} value={s}>{INTEREST_STATUS_LABEL[s]}</option>)}
        </select>
        <span className="text-xs text-ink-soft">{filtered.length} of {items.length}</span>
      </div>

      {filtered.length === 0 ? (
        <div data-testid="interest-tab-empty" className="py-10 text-center text-sm text-ink-soft">No applications or support requests yet.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((i) => (
            <div key={i.id} data-testid={`interest-row-${i.id}`} className="rounded-md border border-hairline p-3.5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink">{i.opportunity?.title || "Opportunity"} <span className="text-ink-soft font-normal">— {i.opportunity?.organisation}</span></div>
                  {i.freelancer ? (
                    <Link to={`/build/f/${i.freelancer.id}`} data-testid={`interest-freelancer-link-${i.id}`} className="text-xs text-ink-soft underline hover:text-ink">{i.freelancer.name || "Freelancer"}{i.freelancer.primary_role ? ` · ${i.freelancer.primary_role}` : ""}</Link>
                  ) : (
                    <span data-testid={`interest-freelancer-link-${i.id}`} className="text-xs text-faint">Freelancer</span>
                  )}
                  <div className="text-[11px] text-faint mt-1 uppercase tracking-wide font-semibold">{i.kind === "application" ? "Application" : "Support request"} · {new Date(i.created_at).toLocaleDateString("en-IN")}</div>
                  {/* Addendum (2026-09-29, founder walkthrough w/ Bavna): explains apparent
                      kind/relationship mismatches on items published before the ingestion
                      default-fix (njm_opportunity -> community) — e.g. "Freelance iPhone
                      Videographer" showing APPLICATION because it was published as
                      njm_opportunity at the time, even though it's since been corrected. */}
                  {i.njm_relationship_at_application && (
                    <div data-testid={`interest-relationship-at-application-${i.id}`} className="text-[11px] text-faint mt-0.5">Listed as {RELATIONSHIP_LABEL[i.njm_relationship_at_application] || i.njm_relationship_at_application} when applied</div>
                  )}
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${STATUS_STYLE[i.status] || "bg-tone-grey-bg text-tone-grey-text"}`}>{INTEREST_STATUS_LABEL[i.status] || i.status}</span>
              </div>

              {i.note && <p className="mt-2 text-[13px] text-ink-soft italic">"{i.note}"</p>}

              <div className="mt-2 flex items-center gap-3 text-[11px] text-faint flex-wrap">
                <span>Availability: {i.flexible ? "Flexible" : `${i.available_from || "?"}${i.available_until ? ` → ${i.available_until}` : ""}`}</span>
                {i.work_url && <a href={i.work_url} target="_blank" rel="noreferrer" className="underline">Work link ↗</a>}
                {(i.work_samples || []).map((s) => <a key={s.id} href={s.url} target="_blank" rel="noreferrer" className="underline">{s.title || "Sample"} ↗</a>)}
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <input data-testid={`interest-reason-${i.id}`} placeholder="Reason / note (optional)" value={reasonDrafts[i.id] || ""} onChange={(e) => setReasonDrafts((d) => ({ ...d, [i.id]: e.target.value }))}
                  className="flex-1 min-w-[160px] rounded-md border border-hairline px-2.5 py-1.5 text-xs" />
                <select data-testid={`interest-status-select-${i.id}`} onChange={(e) => { if (e.target.value) setStatus(i.id, e.target.value); e.target.value = ""; }} defaultValue=""
                  className="rounded-md border border-hairline px-2.5 py-1.5 text-xs">
                  <option value="" disabled>Change status…</option>
                  {INTEREST_STATUSES.map((s) => <option key={s} value={s}>{INTEREST_STATUS_LABEL[s]}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Wave 1 (2026-09-30) — staff-only classification control: set `kind` (njm/member) on any
// existing Collaboration. Read-only list otherwise; no other field here is editable — every
// other Collaboration mutation still goes through the freelancer-facing collaboration.py
// endpoints, unchanged.
const COLLAB_KINDS = ["njm", "member"];
const COLLAB_KIND_LABEL = { njm: "NJM Collaboration", member: "Member Collaboration" };

function CollaborationsKindTab({ initialManageId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [showCreateNjm, setShowCreateNjm] = useState(false);
  const [manageId, setManageId] = useState(initialManageId || null);
  const [autoOpenId] = useState(initialManageId || null);

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/v1/collaboration/opportunities/admin-list").then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("Couldn't load collaborations")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setKind = async (id, kind) => {
    setSavingId(id);
    try {
      await api.patch(`/v1/collaboration/opportunities/${id}/kind`, { kind });
      toast.success("Classification updated");
      load();
    } catch {
      toast.error("Couldn't update classification");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <div className="py-10 text-center text-sm text-ink-soft">Loading…</div>;

  return (
    <div data-testid="collaborations-kind-tab" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p data-testid="collaborations-kind-tab-helper" className="text-xs text-ink-soft">Classify each Collaboration as NJM (paid, NJM-run) or Member (peer-to-peer) — additive only, defaults to Member.</p>
        <button data-testid="collaboration-create-njm-button" onClick={() => setShowCreateNjm(true)}
          className="inline-flex items-center gap-1.5 shrink-0 rounded-md bg-brand px-3 py-2 text-sm font-medium text-ink">
          <Plus className="h-4 w-4" />New NJM Collaboration
        </button>
      </div>
      {items.length === 0 ? (
        <EmptyState title="No collaborations yet" description="They'll show up here once freelancers start them." />
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} data-testid={`collaboration-kind-row-${item.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-hairline p-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink">{item.title}</div>
                <div className="text-xs text-ink-soft">{item.creator_name} · {item.status}{item.kind === "njm" && <span> · Paid · {(item.role_slots || []).length} role{(item.role_slots || []).length === 1 ? "" : "s"}</span>}</div>
              </div>
              <div className="flex items-center gap-2">
                {item.kind === "njm" && (
                  <button data-testid={`collaboration-manage-btn-${item.id}`} onClick={() => setManageId(item.id)}
                    className="rounded-md border border-hairline px-2.5 py-1.5 text-xs font-medium text-ink hover:border-ink/30">Manage</button>
                )}
                <select data-testid={`collaboration-kind-select-${item.id}`} value={item.kind} disabled={savingId === item.id}
                  onChange={(e) => setKind(item.id, e.target.value)} className="rounded-md border border-hairline px-2.5 py-1.5 text-xs disabled:opacity-50">
                  {COLLAB_KINDS.map((k) => <option key={k} value={k}>{COLLAB_KIND_LABEL[k]}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
      {showCreateNjm && (
        <CreateNjmCollaborationModal onClose={() => setShowCreateNjm(false)} onSaved={() => { setShowCreateNjm(false); load(); }} />
      )}
      {manageId && (
        <CollaborationDetailModal opportunityId={manageId} viewerIsFreelancer={false} onClose={() => setManageId(null)} onChanged={load}
          autoOpenApplications={manageId === autoOpenId} />
      )}
    </div>
  );
}

export default function AdminOpportunities() {
  // Dashboard Fixes (2026-11), Item 1/2 — one-time read of the deep-link query params a
  // Dashboard "Needs your attention" item lands on (`?tab=collaborations&collab_id=…` or
  // `?tab=interest`), read via `window.location.search` directly (not `useSearchParams`) so
  // this file's existing `react-router-dom` test mock — which only exports `Link` — keeps
  // working unchanged.
  const [deepLink] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { tab: params.get("tab"), collabId: params.get("collab_id") };
  });
  const [activeTab, setActiveTab] = useState(deepLink.tab || "opportunities");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Production bugfix (2026-09-27): default view opens on "Candidates (needs review)", not
  // "All statuses" — see request #4. "" still means All statuses, unchanged everywhere else.
  const [statusFilter, setStatusFilter] = useState("candidate");
  const [disciplineFilter, setDisciplineFilter] = useState("");
  const [relationshipFilter, setRelationshipFilter] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  // Phase 6 (2026-10, Job D) — the 420px review drawer for a clicked row (frame 11). Opening
  // any other action (Edit/Manage through NJM/Reject/view linked collaboration) closes this
  // first, same pattern the old card buttons already used for `showForm`.
  const [reviewingItem, setReviewingItem] = useState(null);
  const [savingRelationship, setSavingRelationship] = useState(false);
  // Production bugfix (2026-09-27) — root cause: this list's own `items`/`load` state lives in
  // THIS top-level component, which only ever mounts once per page load. Switching between the
  // 3 tabs is a plain ternary re-render (no unmount), so the original `useEffect(() => { load();
  // }, [load])` fired its ONE fetch at initial mount and never again — if that first fetch
  // happened before `feature_build_opportunities` was on (or before ingestion had run), `items`
  // stayed permanently `[]` for the rest of that page load, no matter how many tabs you clicked
  // through or how much data showed up elsewhere (Shadow Ingestion/Interest ARE separate child
  // components that remount — and therefore refetch — every time you click into them, which is
  // why only THIS tab looked stuck). `dataVersion` also bumps after every write anywhere on this
  // page (publish/reject/archive/verify-class/ingestion run) so this list — and the candidate
  // count badge below — never need a manual refresh again.
  const [dataVersion, setDataVersion] = useState(0);
  const bump = () => setDataVersion((v) => v + 1);
  const [candidateCount, setCandidateCount] = useState(0);
  // Wave 4 (2026-09-30) — "Manage through NJM": `manageThroughNjmItem` holds the source
  // External Opportunity while its prefilled NJM Collaboration create form is open;
  // `viewCollabId` opens the (reused) CollaborationDetailModal for an already-promoted one.
  const [manageThroughNjmItem, setManageThroughNjmItem] = useState(null);
  const [viewCollabId, setViewCollabId] = useState(null);
  const [collabTitleById, setCollabTitleById] = useState({});

  useEffect(() => {
    api.get("/v1/collaboration/opportunities/admin-list").then((r) => {
      const map = {};
      (r.data.items || []).forEach((c) => { map[c.id] = c.title; });
      setCollabTitleById(map);
    }).catch(() => {});
  }, [dataVersion]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (disciplineFilter) params.discipline = disciplineFilter;
    if (relationshipFilter) params.njm_relationship = relationshipFilter;
    if (expiringOnly) params.expiring_within_days = 7;
    return api.get("/v1/build-opportunities", { params }).then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("Couldn't load opportunities")).finally(() => setLoading(false));
  }, [statusFilter, disciplineFilter, relationshipFilter, expiringOnly]);

  useEffect(() => { if (activeTab === "opportunities") load(); }, [load, activeTab, dataVersion]);

  useEffect(() => {
    api.get("/v1/build-opportunities", { params: { status: "candidate" } })
      .then((r) => setCandidateCount((r.data.items || []).length)).catch(() => {});
  }, [dataVersion]);

  const act = async (id, action, body) => {
    try {
      await api.post(`/v1/build-opportunities/${id}/${action}`, body || {});
      toast.success(`Opportunity ${action}ed`);
      load();
      bump();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code ? String(e.response.data.detail.code).replaceAll("_", " ") : `Couldn't ${action} this opportunity`);
    }
  };

  // Addendum (2026-09-28, REMOVED Wave 1 2026-09-30): the server no longer 409s with
  // `relationship_confirmation_required` on publish (Nikhil-approved — staff no longer need
  // to confirm the relationship at publish time), so this is now a plain publish call.
  const requestPublish = async (item) => {
    await act(item.id, "publish");
    setShowForm(false);
  };

  const submitReject = async () => {
    if (!rejectReason.trim()) { toast.error("A rejection reason is required"); return; }
    await act(rejectingId, "reject", { reason: rejectReason.trim() });
    setRejectingId(null); setRejectReason("");
  };

  const openItem = (item) => { setReviewingItem(null); setEditing(item); setShowForm(true); };

  // Phase 6 (2026-10, Job D) — relationship picker in the review drawer. Same endpoint the
  // full Edit form already PATCHes (modules/build_opportunities.py `PATCH /{id}` does a
  // partial merge, verified from code — sending only njm_relationship cannot wipe other
  // fields), so this is a faster path to an already-existing, already-editable field, not a
  // new capability.
  const updateRelationship = async (item, value) => {
    setSavingRelationship(true);
    try {
      const r = await api.patch(`/v1/build-opportunities/${item.id}`, { njm_relationship: value });
      setReviewingItem(r.data.item || { ...item, njm_relationship: value });
      load(); bump();
      toast.success("Relationship updated");
    } catch (e) {
      toast.error("Couldn't update the relationship");
    } finally {
      setSavingRelationship(false);
    }
  };

  const onDrawerPublish = (item) => { setReviewingItem(null); requestPublish(item); };
  const onDrawerReject = (item) => { setReviewingItem(null); setRejectingId(item.id); };
  const onDrawerManageThroughNjm = (item) => { setReviewingItem(null); setManageThroughNjmItem(item); };
  const onDrawerClose = (item) => { setReviewingItem(null); act(item.id, "close"); };
  const onDrawerArchive = (item) => { setReviewingItem(null); act(item.id, "archive"); };
  const onViewCollab = (collabId) => { setReviewingItem(null); setViewCollabId(collabId); };

  const sortedItems = sortByExpiryAsc(items);
  const OPPORTUNITY_TABS = [
    { key: "opportunities", stateValue: "opportunities", label: "External Opportunities", count: candidateCount > 0 ? `${candidateCount} to review` : undefined, countTestId: "opportunities-tab-candidate-badge" },
    { key: "shadow-ingestion", stateValue: "shadow_ingestion", label: "Shadow Ingestion" },
    { key: "interest", stateValue: "interest", label: "Support Requests" },
    { key: "collaborations", stateValue: "collaborations", label: "Collaborations" },
  ];

  return (
    <div data-testid="admin-opportunities-page" className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="font-editorial text-3xl sm:text-4xl tracking-tight text-ink">Opportunities</h1>
        {activeTab === "opportunities" && (
          <button data-testid="opportunity-create-button" onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-medium text-ink hover:bg-brand-dim">
            <Plus className="h-4 w-4" />New opportunity
          </button>
        )}
      </div>

      <Tabs
        variant="record"
        testId="opportunities-tab"
        value={OPPORTUNITY_TABS.find((t) => t.stateValue === activeTab)?.key}
        onChange={(key) => setActiveTab(OPPORTUNITY_TABS.find((t) => t.key === key)?.stateValue)}
        tabs={OPPORTUNITY_TABS}
        className="mb-4"
      />

      {activeTab === "shadow_ingestion" ? (
        <ShadowIngestionTab refreshKey={dataVersion} onDataChanged={bump} onOpenItem={openItem} />
      ) : activeTab === "interest" ? (
        <InterestTab />
      ) : activeTab === "collaborations" ? (
        <CollaborationsKindTab initialManageId={deepLink.collabId} />
      ) : (
      <>
      <p data-testid="opportunities-tab-helper" className="mb-3 text-xs text-ink-soft">Grok's daily finds land here as candidates. Open one, check it, set the relationship, then Publish or Reject. Freelancers only see Published.</p>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="flex flex-wrap gap-2">
          <select data-testid="opportunity-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-md border border-hairline px-2.5 py-1.5 text-xs bg-paper text-ink">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s === "candidate" ? "Candidates (needs review)" : s}</option>)}
          </select>
          <select data-testid="opportunity-filter-discipline" value={disciplineFilter} onChange={(e) => setDisciplineFilter(e.target.value)} className="rounded-md border border-hairline px-2.5 py-1.5 text-xs bg-paper text-ink">
            <option value="">All disciplines</option>
            {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select data-testid="opportunity-filter-relationship" value={relationshipFilter} onChange={(e) => setRelationshipFilter(e.target.value)} className="rounded-md border border-hairline px-2.5 py-1.5 text-xs bg-paper text-ink">
            <option value="">All relationships</option>
            {NJM_RELATIONSHIPS.map((r) => <option key={r} value={r}>{r.replaceAll("_", " ")}</option>)}
          </select>
          <button type="button" data-testid="opportunity-filter-expiring-soon" onClick={() => setExpiringOnly((v) => !v)} className={`rounded-md border px-2.5 py-1.5 text-xs ${expiringOnly ? "border-brand bg-brand/15 text-ink" : "border-hairline text-ink-soft"}`}>Expiring soon</button>
        </div>
        {!loading && items.length > 0 && (
          <span data-testid="opportunities-count-line" className="text-xs text-faint whitespace-nowrap">
            {sortedItems.length} {statusFilter === "candidate" ? "to review" : "opportunities"} · sorted by expiry
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-ink-soft">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No opportunities yet" description="Create the first one to get started." />
      ) : (
        <div className="rounded-md border border-hairline overflow-x-auto" data-testid="opportunities-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-hairline bg-surface2 text-[11px] uppercase tracking-wider text-faint">
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Location</th>
                <th className="px-3 py-2 font-medium">Disciplines</th>
                <th className="px-3 py-2 font-medium">Expires</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item) => (
                <OpportunityTableRow key={item.id} item={item}
                  onOpen={setReviewingItem}
                  onPublish={onDrawerPublish} onReject={onDrawerReject}
                  onEdit={openItem} onManageThroughNjm={onDrawerManageThroughNjm}
                  onClose={onDrawerClose} onArchive={onDrawerArchive}
                  onViewCollab={onViewCollab} collabTitleById={collabTitleById}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {reviewingItem && (
        <OpportunityReviewDrawer
          item={reviewingItem}
          onClose={() => setReviewingItem(null)}
          onPublish={onDrawerPublish} onReject={onDrawerReject}
          onEdit={openItem} onManageThroughNjm={onDrawerManageThroughNjm}
          onCloseOpportunity={onDrawerClose} onArchive={onDrawerArchive}
          onRelationshipChange={updateRelationship} savingRelationship={savingRelationship}
          onViewCollab={onViewCollab} collabTitleById={collabTitleById}
        />
      )}

      {showForm && (
        <OpportunityForm
          initial={editing || EMPTY_FORM}
          onCancel={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); bump(); }}
          onPublish={editing ? () => requestPublish(editing) : undefined}
          onReject={editing ? () => { setShowForm(false); setRejectingId(editing.id); } : undefined}
          onManageThroughNjm={editing ? () => { setShowForm(false); setManageThroughNjmItem(editing); } : undefined}
          collabTitle={editing?.promoted_to_collaboration_id ? collabTitleById[editing.promoted_to_collaboration_id] : null}
          onViewCollab={editing?.promoted_to_collaboration_id ? () => { setShowForm(false); setViewCollabId(editing.promoted_to_collaboration_id); } : undefined}
        />
      )}

      {manageThroughNjmItem && (
        <CreateNjmCollaborationModal
          initialFromOpportunity={manageThroughNjmItem}
          onClose={() => setManageThroughNjmItem(null)}
          onSaved={() => { setManageThroughNjmItem(null); load(); bump(); }}
        />
      )}

      {viewCollabId && (
        <CollaborationDetailModal opportunityId={viewCollabId} viewerIsFreelancer={false} onClose={() => setViewCollabId(null)} onChanged={() => { load(); bump(); }} />
      )}

      {rejectingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-paper p-4 shadow-xl">
            <div className="mb-2 text-sm font-semibold text-ink">Reject opportunity</div>
            <textarea data-testid="opportunity-reject-reason-input" autoFocus value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (required)" rows={3} className="w-full rounded-md border border-hairline px-3 py-2 text-sm bg-paper text-ink" />
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" data-testid="opportunity-reject-cancel" onClick={() => { setRejectingId(null); setRejectReason(""); }} className="rounded-md border border-hairline px-3 py-1.5 text-sm text-ink-soft">Cancel</button>
              <button type="button" data-testid="opportunity-reject-confirm" onClick={submitReject} className="rounded-md bg-tone-red-text px-3 py-1.5 text-sm font-medium text-paper">Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Addendum (2026-09-28): publish-time relationship confirmation removed Wave 1
          (2026-09-30) — staff no longer need to confirm the relationship before publishing. */}
    </div>
  );
}
